/**
 * Footer — Footer global avec colonnes Produit / Légal / Confiance / Contact
 */

import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { openCookieSettings } from "./CookieBanner";
import { useBeta } from "../hooks/useBeta";

const CONTACT_EMAIL = "contact@wana-family.fr";

// Vérifié plutôt que supposé : ces liens ont bien 17 px de hauteur CLIQUABLE,
// `padding: 0`, séparés de 8 px. Ce n'est pas la taille du texte qui est en
// cause, c'est l'absence totale de zone autour. `py-2` porte la cible à 33 px
// sans changer d'un pixel l'aspect du pied de page — le texte ne bouge pas,
// seule la surface sensible grandit.
const STYLE_LIEN =
  "block min-h-11 py-3 text-gray-400 hover:text-white transition-colors text-sm text-left " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 rounded-sm";

/**
 * Une destination est un LIEN, pas un bouton.
 *
 * Tout le pied de page naviguait par `onClick={() => navigate(...)}` : mesuré
 * sur l'accueil, 2 ancres pour 16 boutons. Conséquences concrètes — pas
 * d'ouverture dans un nouvel onglet, pas de copie d'adresse, rien à donner aux
 * moteurs, et un lecteur d'écran qui annonce « bouton » là où il faut annoncer
 * « lien ». `<Link>` rend une vraie <a href> tout en gardant la navigation
 * client.
 *
 * `FooterAction` reste un bouton, et c'est volontaire : « Gérer les cookies »
 * ouvre un panneau, il ne mène nulle part.
 */
function FooterLink({ to, children }) {
  return (
    <Link to={to} className={STYLE_LIEN}>
      {children}
    </Link>
  );
}

function FooterAction({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} className={STYLE_LIEN}>
      {children}
    </button>
  );
}

function Footer() {
  // Même raison que dans la barre de navigation : pendant la bêta,
  // `/pricing` ne contient aucun prix.
  const beta = useBeta();

  return (
    <footer className="bg-gray-900 text-gray-300 pt-12 pb-8 mt-12">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              {/* La MÊME marque que la navigation. Une toque de diplômé
                  cohabitait ici avec la tuile « M » de l'en-tête : deux signes
                  pour un seul produit, sur la même page. */}
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary font-serif text-sm font-bold text-primary-foreground">M</span>
              <span className="text-white font-bold">Le Maître Mot</span>
            </div>
            <p className="text-sm text-gray-400 mb-4 max-w-xs">
              Générateur d'exercices de maths pour le primaire et le collège,
              aligné sur le programme officiel.
            </p>
            <Link
              to="/confiance"
              className="inline-flex items-center gap-2 rounded-full border border-gray-700 px-3 py-1 text-xs text-gray-400 transition-colors hover:border-gray-500 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Zéro donnée d'élève
            </Link>
          </div>

          {/* Produit */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">Produit</h4>
            <ul className="space-y-0.5">
              <li><FooterLink to="/">Créer une fiche</FooterLink></li>
              <li><FooterLink to="/pricing">{beta.active ? "La bêta" : "Tarifs"}</FooterLink></li>
              <li><FooterLink to="/a-propos">À propos</FooterLink></li>
            </ul>
          </div>

          {/* Légal */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">Légal</h4>
            <ul className="space-y-0.5">
              <li><FooterLink to="/mentions-legales">Mentions légales</FooterLink></li>
              <li><FooterLink to="/cgu">CGU</FooterLink></li>
              {/* « CGV » annoncerait des conditions de VENTE ; pendant la bêta,
                  la page sert des conditions de bêta. */}
              <li><FooterLink to="/cgv">{beta.active ? "Conditions de la bêta" : "CGV"}</FooterLink></li>
              <li><FooterLink to="/politique-confidentialite">Confidentialité</FooterLink></li>
              <li><FooterLink to="/confiance">Confiance &amp; conformité</FooterLink></li>
              <li><FooterAction onClick={openCookieSettings}>Gérer les cookies</FooterAction></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">Contact</h4>
            <ul className="space-y-0.5">
              <li>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  {CONTACT_EMAIL}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-6 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} Le Maître Mot. Tous droits réservés.
        </div>
      </div>
    </footer>
  );
}

export default Footer;
